import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  Divider,
  MenuItem,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StarIcon from "@mui/icons-material/Star";
import {
  ADMIN_GOOGLE_REVIEWS_QUERY,
  ADMIN_UPSERT_GOOGLE_REVIEW_MUTATION,
  ADMIN_DELETE_GOOGLE_REVIEW_MUTATION,
} from "../../graphql/googleReviews";

type ReviewRow = {
  id: string;
  reviewerName: string;
  rating: number;
  reviewText: string;
  reviewDate?: string | null;
  active: boolean;
  position: number;
};

type ReviewForm = {
  id?: string;
  reviewerName: string;
  rating: number;
  reviewText: string;
  reviewDate: string;
  active: boolean;
  position: number;
};

const emptyForm: ReviewForm = {
  reviewerName: "",
  rating: 5,
  reviewText: "",
  reviewDate: "",
  active: true,
  position: 0,
};

export default function GoogleReviewsAdminPage() {
  const { data, loading, error, refetch } = useQuery(
    ADMIN_GOOGLE_REVIEWS_QUERY,
    { fetchPolicy: "cache-and-network" },
  );

  const [upsert, upsertState] = useMutation(
    ADMIN_UPSERT_GOOGLE_REVIEW_MUTATION,
  );
  const [deleteReview, deleteState] = useMutation(
    ADMIN_DELETE_GOOGLE_REVIEW_MUTATION,
  );

  const [openForm, setOpenForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ReviewRow | null>(null);
  const [form, setForm] = useState<ReviewForm>(emptyForm);

  const reviews: ReviewRow[] = data?.adminGoogleReviews ?? [];

  const openCreate = () => {
    setForm(emptyForm);
    setOpenForm(true);
  };

  const openEdit = (r: ReviewRow) => {
    setForm({
      id: r.id,
      reviewerName: r.reviewerName,
      rating: r.rating,
      reviewText: r.reviewText,
      reviewDate: r.reviewDate ?? "",
      active: r.active,
      position: r.position,
    });
    setOpenForm(true);
  };

  const handleSave = async () => {
    const res = await upsert({
      variables: {
        id: form.id || null,
        reviewerName: form.reviewerName.trim(),
        rating: form.rating,
        reviewText: form.reviewText.trim(),
        reviewDate: form.reviewDate.trim() || null,
        active: form.active,
        position: form.position,
      },
    });

    const errors = res?.data?.adminUpsertGoogleReview?.errors ?? [];
    if (errors.length) return;

    setOpenForm(false);
    await refetch();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await deleteReview({ variables: { id: confirmDelete.id } });
    setConfirmDelete(null);
    await refetch();
  };

  const formErrors: string[] =
    upsertState.data?.adminUpsertGoogleReview?.errors ?? [];

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        mb={2}
        spacing={2}
      >
        <Box>
          <Typography variant="h5">Google Reviews</Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Add your best Google reviews here. They will display as a carousel
            on the products listing page. Copy reviews directly from your Google
            Maps business profile.
          </Alert>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Add Review
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <Paper>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }}>Pos</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Reviewer</TableCell>
                <TableCell sx={{ width: 120 }}>Rating</TableCell>
                <TableCell>Review</TableCell>
                <TableCell sx={{ width: 120 }}>Date</TableCell>
                <TableCell sx={{ width: 80 }}>Status</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviews.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No reviews added yet. Click "Add Review" to get started.
                  </TableCell>
                </TableRow>
              )}
              {reviews.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.position}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {r.reviewerName}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <StarIcon sx={{ fontSize: 16, color: "#F4B942" }} />
                      <Typography sx={{ fontSize: 13 }}>
                        {r.rating}/5
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        maxWidth: 320,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        fontSize: 13,
                        color: "text.secondary",
                      }}
                    >
                      {r.reviewText}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {r.reviewDate ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.active ? "Active" : "Hidden"}
                      color={r.active ? "success" : "default"}
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setConfirmDelete(r)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* FORM DIALOG */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            {form.id ? "Edit Review" : "Add Review"}
          </Typography>
          <Divider sx={{ my: 2 }} />

          {formErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.join(" • ")}
            </Alert>
          )}

          <Stack spacing={2}>
            <TextField
              label="Reviewer Name"
              value={form.reviewerName}
              onChange={(e) =>
                setForm((p) => ({ ...p, reviewerName: e.target.value }))
              }
              fullWidth
              placeholder="e.g. Ahmed Khan"
            />

            <TextField
              select
              label="Star Rating"
              value={form.rating}
              onChange={(e) =>
                setForm((p) => ({ ...p, rating: Number(e.target.value) }))
              }
              fullWidth
            >
              {[5, 4, 3, 2, 1].map((r) => (
                <MenuItem key={r} value={r}>
                  {"⭐".repeat(r)} ({r}/5)
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Review Text"
              value={form.reviewText}
              onChange={(e) =>
                setForm((p) => ({ ...p, reviewText: e.target.value }))
              }
              fullWidth
              multiline
              minRows={4}
              placeholder="Copy the review text from Google Maps..."
            />

            <TextField
              label="Review Date"
              value={form.reviewDate}
              onChange={(e) =>
                setForm((p) => ({ ...p, reviewDate: e.target.value }))
              }
              fullWidth
              placeholder="e.g. March 2024"
              helperText="Write the date as shown on Google Maps e.g. 'a month ago' or 'March 2024'"
            />

            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Status"
                value={form.active ? "active" : "hidden"}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    active: e.target.value === "active",
                  }))
                }
                fullWidth
              >
                <MenuItem value="active">Active (Visible)</MenuItem>
                <MenuItem value="hidden">Hidden</MenuItem>
              </TextField>

              <TextField
                label="Display Order"
                type="number"
                value={form.position}
                onChange={(e) =>
                  setForm((p) => ({ ...p, position: Number(e.target.value) }))
                }
                fullWidth
                helperText="Lower number = shown first"
              />
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={() => setOpenForm(false)}
              disabled={upsertState.loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                upsertState.loading ||
                !form.reviewerName.trim() ||
                !form.reviewText.trim()
              }
            >
              {upsertState.loading ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* DELETE CONFIRM */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            Delete Review?
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            This will permanently delete the review by{" "}
            <b>{confirmDelete?.reviewerName}</b>.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={() => setConfirmDelete(null)}
              disabled={deleteState.loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleteState.loading}
            >
              {deleteState.loading ? "Deleting..." : "Delete"}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}
